import { useMutation, useQueryClient } from '@tanstack/react-query';
import { History, Pencil, Power, PowerOff, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Textarea } from '@/components/ui/Textarea';
import { useHasRole } from '@/lib/auth/AuthProvider';
import { sourcesApi } from '@/lib/api';
import type { Source } from '@/types/api';

import { RssPollHistoryDialog } from './RssPollHistoryDialog';

interface SourceActionsProps {
  source: Source;
  onEdit: (s: Source) => void;
  onMessage: (msg: string) => void;
}

/**
 * Per-row action menu. Edit opens the AddSourceDialog in edit mode.
 * Deactivate / reactivate are role-gated to analysts; delete requires
 * the administrator role and is hidden otherwise.
 */
export function SourceActions({ source, onEdit, onMessage }: SourceActionsProps) {
  const { t } = useTranslation('studio');
  const qc = useQueryClient();
  const isAdmin = useHasRole('administrator');

  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [reason, setReason] = useState('');

  const isRss = source.source_type === 'rss_feed';

  const pollNow = useMutation({
    mutationFn: () => sourcesApi.pollNow(source.source_id),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['studio', 'sources'] });
      qc.invalidateQueries({
        queryKey: ['studio', 'sources', source.source_id, 'poll-history'],
      });
      onMessage(
        t('sources.poll.successMessage', {
          new: result.items_new,
          ingested: result.items_ingested,
        }),
      );
    },
    onError: (err: unknown) => {
      onMessage(
        t('sources.poll.errorMessage', {
          reason: err instanceof Error ? err.message : 'unknown',
        }),
      );
    },
  });

  const deactivate = useMutation({
    mutationFn: (r: string | undefined) => sourcesApi.deactivate(source.source_id, r),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['studio', 'sources'] });
      setDeactivateOpen(false);
      setReason('');
      onMessage(t('sources.deactivate.successMessage'));
    },
  });

  const reactivate = useMutation({
    mutationFn: () => sourcesApi.reactivate(source.source_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['studio', 'sources'] }),
  });

  const remove = useMutation({
    mutationFn: () => sourcesApi.remove(source.source_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['studio', 'sources'] });
      setDeleteOpen(false);
      onMessage(t('sources.delete.successMessage'));
    },
  });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('sources.actions.open')}
          >
            ⋯
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => onEdit(source)}>
            <Pencil className="mr-2 h-3.5 w-3.5" aria-hidden />
            {t('sources.actions.edit')}
          </DropdownMenuItem>
          {isRss && (
            <>
              <DropdownMenuItem
                onSelect={() => pollNow.mutate()}
                disabled={pollNow.isPending || !source.active}
              >
                <RefreshCw
                  className={`mr-2 h-3.5 w-3.5 ${pollNow.isPending ? 'animate-spin' : ''}`}
                  aria-hidden
                />
                {pollNow.isPending
                  ? t('sources.poll.polling')
                  : t('sources.poll.pollNow')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setHistoryOpen(true)}>
                <History className="mr-2 h-3.5 w-3.5" aria-hidden />
                {t('sources.poll.history')}
              </DropdownMenuItem>
            </>
          )}
          {source.active ? (
            <DropdownMenuItem onSelect={() => setDeactivateOpen(true)}>
              <PowerOff className="mr-2 h-3.5 w-3.5" aria-hidden />
              {t('sources.actions.deactivate')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onSelect={() => reactivate.mutate()}
              disabled={reactivate.isPending}
            >
              <Power className="mr-2 h-3.5 w-3.5" aria-hidden />
              {t('sources.actions.reactivate')}
            </DropdownMenuItem>
          )}
          {isAdmin && (
            <DropdownMenuItem
              onSelect={() => setDeleteOpen(true)}
              className="text-red-700 focus:bg-red-50 focus:text-red-800"
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" aria-hidden />
              {t('sources.actions.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={deactivateOpen} onOpenChange={setDeactivateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sources.deactivate.title')}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              deactivate.mutate(reason.trim() || undefined);
            }}
            className="space-y-3"
          >
            <p className="text-small text-slate-600">{t('sources.deactivate.description')}</p>
            <label className="block text-small">
              <span className="text-slate-700">{t('sources.deactivate.reason')}</span>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDeactivateOpen(false)}
                disabled={deactivate.isPending}
              >
                {t('sources.add.cancel')}
              </Button>
              <Button type="submit" disabled={deactivate.isPending}>
                {t('sources.deactivate.submit')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <RssPollHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        sourceId={source.source_id}
        sourceUrl={source.source_url}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sources.delete.title')}</DialogTitle>
          </DialogHeader>
          <p className="text-small text-slate-600">{t('sources.delete.description')}</p>
          <p className="mt-2 break-all font-mono text-small text-slate-500">
            {source.source_url}
          </p>
          <div className="mt-3 flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setDeleteOpen(false)}
              disabled={remove.isPending}
            >
              {t('sources.add.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {t('sources.delete.confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
